/**
 * B2B Variant Filter
 *
 * Ensures the correct variant is displayed and selected based on customer type:
 * - B2B customers: variants with custom.einzelverkauf = true (Einzelstück) are hidden
 * - B2C customers: every variant stays visible
 *
 * This works in conjunction with the Liquid-side filtering in:
 * - snippets/b2b-variant-filter.liquid  (source of truth for the JSON payload)
 * - snippets/product-variant-options.liquid
 * - snippets/price.liquid
 * - snippets/quick-order-list.liquid
 */

(function() {
  'use strict';

  // Check if we're on a product page or have product cards
  const initB2BVariantFilter = () => {
    // Read B2B filter data from any data elements on the page
    const filterDataElements = document.querySelectorAll('[data-b2b-variant-filter]');
    
    filterDataElements.forEach(element => {
      try {
        const filterData = JSON.parse(element.textContent);
        const productId = element.dataset.productId;
        
        if (!filterData || !filterData.filteredVariantIds || filterData.filteredVariantIds.length === 0) {
          return;
        }

        // Find the product form for this product. The JSON payload lives
        // inside <product-info data-product-id="…"> in the theme; the actual
        // <product-form> was previously not carrying data-product-id, which
        // is why the old lookup silently failed. We now:
        //   1. Look for a form with the matching data-product-id (new)
        //   2. Fall back to the nearest ancestor container that has one
        //   3. Fall back to a form anywhere on the page (single-product pages)
        let productForm =
          document.querySelector(`form[action*="/cart/add"][data-product-id="${productId}"]`) ||
          document.querySelector(`product-form[data-product-id="${productId}"] form[action*="/cart/add"]`);

        const scope = element.closest(`[data-product-id="${productId}"]`) || document;
        if (!productForm) {
          productForm = scope.querySelector('form[action*="/cart/add"]');
        }

        // Ensure the variant input has a valid filtered variant ID
        if (productForm) {
          const variantInput = productForm.querySelector('input[name="id"]');
          if (variantInput) {
            const currentVariantId = parseInt(variantInput.value);

            // If current variant is not in filtered list, switch to default
            if (!filterData.filteredVariantIds.includes(currentVariantId) && filterData.defaultVariantId) {
              variantInput.value = filterData.defaultVariantId;

              // Trigger change event to update UI
              variantInput.dispatchEvent(new Event('change', { bubbles: true }));
            }
          }
        }

        // If the picker has no radio checked (because Shopify's default variant
        // was filtered out of the DOM), click the first visible radio so the
        // theme's own variant-swap flow runs and updates price/media/form.
        forceSelectFilteredVariantOnPicker(scope, filterData);

        // Hide variant options that shouldn't be visible (backup for JS-rendered content)
        hideNonFilteredOptions(productId, filterData);

      } catch (e) {
        console.error('B2B Variant Filter: Error parsing filter data', e);
      }
    });
  };

  /**
   * Ensure the variant-selects picker has one of the filtered variants selected.
   * Delegates to the theme's own variant-swap machinery by triggering a real
   * change event on the swatch input, which is what a user click would do.
   */
  const forceSelectFilteredVariantOnPicker = (scope, filterData) => {
    const variantSelects = scope.querySelector('variant-selects');
    if (!variantSelects) return;

    const productForm = scope.querySelector('form[action*="/cart/add"]');
    const variantInput = productForm && productForm.querySelector('input[name="id"]');
    const currentId = variantInput ? parseInt(variantInput.value, 10) : NaN;

    // Already good? Nothing to do.
    if (filterData.filteredVariantIds && filterData.filteredVariantIds.indexOf(currentId) !== -1) {
      const alreadyChecked = variantSelects.querySelector('input[type="radio"]:checked, option[selected]');
      if (alreadyChecked) return;
    }

    // Prefer radio inputs (swatch/button pickers)
    const radios = variantSelects.querySelectorAll('input[type="radio"]');
    if (radios.length > 0) {
      const preferred = Array.from(radios).find(
        (r) => !r.disabled && !r.classList.contains('disabled') && !r.checked
      ) || radios[0];
      // Dispatch a real change event so <variant-selects>.onVariantChange runs
      preferred.checked = true;
      preferred.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    // Dropdown picker fallback
    const select = variantSelects.querySelector('select');
    if (select) {
      const preferred = Array.from(select.options).find(
        (o) => !o.disabled && !o.hidden && o.style.display !== 'none'
      );
      if (preferred) {
        select.value = preferred.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  };
  
  /**
   * Hide variant options that don't match the B2B filter
   * This is a backup for cases where Liquid filtering wasn't applied (e.g., AJAX-loaded content)
   */
  const hideNonFilteredOptions = (productId, filterData) => {
    const availableValues = filterData.availableOptionValues || [];
    
    if (availableValues.length === 0) return;
    
    // Find all variant selectors for this product
    const variantSelectors = document.querySelectorAll(`[data-product-id="${productId}"] .product-form__input`);
    
    variantSelectors.forEach(selector => {
      const inputs = selector.querySelectorAll('input[type="radio"], select option');
      
      inputs.forEach(input => {
        const value = input.value || input.textContent.trim();
        
        // If this value is not in the available list, hide it
        if (!availableValues.some(av => av.toLowerCase() === value.toLowerCase())) {
          if (input.tagName === 'INPUT') {
            const label = input.closest('label') || document.querySelector(`label[for="${input.id}"]`);
            if (label) {
              label.style.display = 'none';
            }
            input.style.display = 'none';
          } else if (input.tagName === 'OPTION') {
            input.style.display = 'none';
            input.disabled = true;
          }
        }
      });
    });
  };

  /**
   * Handle AJAX cart additions - ensure correct variant is added
   */
  const interceptCartAdditions = () => {
    // Intercept fetch requests to /cart/add
    const originalFetch = window.fetch;
    
    window.fetch = function(url, options) {
      if (typeof url === 'string' && url.includes('/cart/add')) {
        // Check if we need to validate the variant being added
        // This is handled server-side, but we could add client-side validation here
      }
      
      return originalFetch.apply(this, arguments);
    };
  };

  /**
   * Re-run filtering when new content is loaded (e.g., quick view modals)
   */
  const observeNewContent = () => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            // Check if new B2B filter data was added
            const newFilterData = node.querySelectorAll ? 
              node.querySelectorAll('[data-b2b-variant-filter]') : [];
            
            if (newFilterData.length > 0) {
              initB2BVariantFilter();
            }
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  };

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initB2BVariantFilter();
      observeNewContent();
    });
  } else {
    initB2BVariantFilter();
    observeNewContent();
  }

  // Expose for manual triggering if needed
  window.B2BVariantFilter = {
    init: initB2BVariantFilter
  };
})();
